import {
  IntervalJSON,
  JSONSerializable,
  ScatterPlotJSON,
  TimeseriesJSON,
} from '@highimpact-frontend/api-types';
import { StateComponent } from '@highimpact/dashboard-ui';
import {
  buildCsvDownloadFilename,
  DEFAULT_DECIMATION_POLICY,
  downloadCsvText,
  rangeBoundsToTimeSelectionMs,
  resolveDefaultBounds,
  useRefreshIntervalFromIntervals,
  useTimeseriesRangeData,
} from '@highimpact/react-hooks';
import {
  buildIntervalFromTimeUnit,
  createInitialPlotPreferences,
  findNextRequestIntervalStart,
  ScatterPlotComponent as Plot,
  refreshIntervalOptionToMs,
  updateToolBarPreferences,
  upgradePlotPreferences,
  useAxisSessionRanges,
  useCurveSessionVisibility,
  type ExportCsvFormValues,
} from '@highimpact/scatterplot';
import {
  adaptThresholdSettingsToPlotly,
  buildThresholdPlotSettingsCore,
} from '@highimpact/thresholds';
import { findTimeRangeType } from '@highimpact/time-interval-selector';
import { resolveIntervalToFixed } from '@highimpact/time-intervals';
import {
  forwardRef,
  LegacyRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { filterWithKeys } from '../../../../internal/utils/record';
import { useSourceLabelsQuery } from '../../../../query/useSourceLabelsQuery';
import { useDashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import { buildScatterCurveInitFromElement } from '../../../../runtime/buildScatterCurveInitFromElement';
import { getConfiguredPlotType } from '../../../../runtime/elementMeasureSupport';
import { type MeasureRow } from '../../../../runtime/measureRow.types';
import {
  getIntervals,
  setAdditionalProperties,
} from '../../../../state/dashboard-element-operations';
import { setDashboardElement } from '../../../../state/dashboard/slice';
import UnconfiguredWidgetPlaceholder from '../../commons/UnconfiguredWidgetPlaceholder';
import {
  getDefaultConfig,
  ScatterPlotProps,
} from '../../commons/plots/helpers';
import { buildExportCsvTransportRequest } from './buildExportCsvTransportRequest';
import { usePlotMeasureTransport } from './usePlotMeasureTransport';
import { useScatterPlotInstanceRegistration } from './useScatterPlotInstanceRegistration';

// -- functions --

function getInitialTimeSelection(
  intervals: IntervalJSON[] | undefined
): [number, number] | undefined {
  if (!intervals || intervals.length === 0) {
    return undefined;
  }

  const resolved = resolveIntervalToFixed(intervals[0]);
  const start = new Date(resolved.startdate).getTime();
  const end = new Date(resolved.enddate).getTime();

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return undefined;
  }
  return [start, end];
}

/** Convert measure API rows into the scatterplot timestamp-keyed data map. */
export function measureRowsToDataMap(
  rows: MeasureRow[] | null | undefined
): Map<Date, Record<string, number>> {
  const acc = new Map<Date, Record<string, number>>();
  if (!rows) {
    return acc;
  }

  rows.forEach((rec) => {
    const rawTimestamp = rec.Temps;
    if (typeof rawTimestamp !== 'string') {
      return;
    }

    const timestamp = new Date(rawTimestamp);
    if (Number.isNaN(timestamp.getTime())) {
      return;
    }

    const recWithoutHorodate = filterWithKeys(
      (k) => k !== 'Temps',
      rec
    ) as Record<string, number>;
    acc.set(timestamp, recWithoutHorodate);
  });

  return acc;
}

// -- component --

function ScatterPlot(
  {
    dataset: _dataset,
    widget_id,
    gridId,
    dashboardElementId,
    elementData,
    campaigns,
  }: ScatterPlotProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useDashboardLibraryDispatch();
  const plotName = elementData.name ?? 'Plot';
  const configuredPlotType = getConfiguredPlotType(elementData);
  const plotTypeForRequest =
    configuredPlotType === 'scatter' ? 'scatter' : 'timeseries';
  const mode = plotTypeForRequest === 'scatter' ? 'scatter' : 'timeline';
  const isScatterPlot = plotTypeForRequest === 'scatter';

  const additionalProperties = useMemo(
    () =>
      (elementData.properties as ScatterPlotJSON | TimeseriesJSON)
        .additional_properties as
        | ReturnType<typeof createInitialPlotPreferences>
        | undefined,
    [elementData.properties]
  );

  const needsTimeseriesCurveInitFallback =
    !isScatterPlot && additionalProperties === undefined;

  const { labelMap, loading: labelsLoading } = useSourceLabelsQuery(
    elementData,
    { enabled: needsTimeseriesCurveInitFallback }
  );

  /** The default intervals for the {@link useTimeseriesRangeData} hook. */
  const defaultIntervals = useMemo(
    () => getIntervals(elementData) ?? [],
    [elementData]
  );

  /** The range type for the {@link useTimeseriesRangeData} hook. */
  const rangeType = useMemo(
    () => findTimeRangeType(defaultIntervals, campaigns)?.kind ?? null,
    [campaigns, defaultIntervals]
  );

  const curveInitSettings = useMemo(
    () =>
      needsTimeseriesCurveInitFallback
        ? buildScatterCurveInitFromElement(
            elementData,
            labelMap,
            plotTypeForRequest
          )
        : [],
    [
      elementData,
      labelMap,
      needsTimeseriesCurveInitFallback,
      plotTypeForRequest,
    ]
  );

  const resolvedPlotPreferences = useMemo(() => {
    if (isScatterPlot) {
      if (!additionalProperties) {
        return createInitialPlotPreferences([]);
      }

      return upgradePlotPreferences(additionalProperties);
    }

    const defaultPrefs = createInitialPlotPreferences(curveInitSettings);

    return upgradePlotPreferences(additionalProperties ?? defaultPrefs);
  }, [additionalProperties, curveInitSettings, isScatterPlot]);

  const decimationPolicy = DEFAULT_DECIMATION_POLICY;

  /** Session-cache transport built from element JSON (Phase 4.5). */
  const { transport, measureRequestSignature } = usePlotMeasureTransport({
    element: elementData,
    plotType: plotTypeForRequest,
    thresholdRequest: resolvedPlotPreferences.thresholdPrefs?.request,
    decimationPolicy,
  });

  /** Range data state as managed by the {@link useTimeseriesRangeData} hook. */
  const {
    data: measureRows,
    loadedRange,
    visibleRange,
    visibleDataBeginIndex,
    loading: rangeHookLoading,
    error: rangeHookError,
    applyVisibleRangeChange,
    applyPredefinedRange,
    refresh,
    resetToDefault,
    setDefaultRange,
  } = useTimeseriesRangeData<MeasureRow>({
    defaultRange: defaultIntervals,
    transport,
    getTimestamp: (row) =>
      typeof row.Temps === 'string' ? row.Temps : String(row.Temps ?? ''),
    decimationPolicy: decimationPolicy,
    rangeType,
  });

  const isLoading =
    (needsTimeseriesCurveInitFallback && labelsLoading) ||
    rangeHookLoading ||
    (measureRows === null && rangeHookError === null);
  const loadError = rangeHookError;

  const visibleInterval = useMemo<[number, number] | undefined>(() => {
    if (visibleRange) {
      return rangeBoundsToTimeSelectionMs(visibleRange);
    }

    return getInitialTimeSelection(defaultIntervals);
  }, [defaultIntervals, visibleRange]);

  const loadedInterval = useMemo<[number, number] | undefined>(() => {
    if (loadedRange) {
      return rangeBoundsToTimeSelectionMs(loadedRange);
    }

    return getInitialTimeSelection(defaultIntervals);
  }, [defaultIntervals, loadedRange]);

  const grid_type = window.location.pathname.includes('tableau-de-bord')
    ? 'grids'
    : window.location.pathname.includes('rapports')
    ? 'reports'
    : 'grids';

  const lastDoubleClickMsRef = useRef(0);

  const data = useMemo(() => measureRowsToDataMap(measureRows), [measureRows]);

  /** Export the plot data as CSV using the options chosen in the export dialog. */
  const handleExportCsv = useCallback(
    async (formValues: ExportCsvFormValues) => {
      const csvText = await transport.exportCsv(
        buildExportCsvTransportRequest({
          formValues,
          curvePrefs: resolvedPlotPreferences.curvePrefs,
          curveToSourceIndex: resolvedPlotPreferences.curveToSourceIndex ?? {},
          timeRanges: {
            loaded: loadedInterval,
            visible: visibleInterval,
          },
        })
      );
      downloadCsvText(csvText, buildCsvDownloadFilename(plotName));
    },
    [
      plotName,
      loadedInterval,
      resolvedPlotPreferences.curvePrefs,
      resolvedPlotPreferences.curveToSourceIndex,
      transport,
      visibleInterval,
    ]
  );

  const plotType = mode === 'timeline' ? 'timeseries' : mode;

  const { csvDialog } = useScatterPlotInstanceRegistration({
    dashboardElementId,
    widgetId: widget_id,
    plotName,
    transport,
    curvePrefs: resolvedPlotPreferences.curvePrefs,
    curveToSourceIndex: resolvedPlotPreferences.curveToSourceIndex ?? {},
    loadedInterval,
    visibleInterval,
    enabled: !isLoading && !loadError,
  });

  const { axisSessionRanges, onChangeAxisRange, resetZoomableAxes } =
    useAxisSessionRanges({
      plotPreferences: resolvedPlotPreferences,
      plotType,
    });

  const {
    curveSessionVisibility,
    toggleCurveSessionVisibility,
    resetCurveSessionVisibility,
  } = useCurveSessionVisibility({
    plotPreferences: resolvedPlotPreferences,
  });

  /** Disable time range pills and refresh interval selector if range type is not relative. */
  const plotPreferencesForRender = useMemo(() => {
    const isRelativeRange = rangeType === 'relative';

    return {
      ...resolvedPlotPreferences,
      toolBarPrefs: {
        ...resolvedPlotPreferences.toolBarPrefs,
        useTimeRangePills:
          resolvedPlotPreferences.toolBarPrefs.useTimeRangePills &&
          isRelativeRange,
        useRefreshIntervalSelector:
          resolvedPlotPreferences.toolBarPrefs.useRefreshIntervalSelector &&
          isRelativeRange,
      },
    };
  }, [rangeType, resolvedPlotPreferences]);

  const measureRequestSignatureValue = measureRequestSignature ?? '';

  /**
   * Reference to the serialized measure request signature.
   *
   * Used to detect config changes that affect the measures API payload
   * (sources, default intervals, threshold request context) and refetch data.
   * Note: null as initial value to skip the first render; hook init already loads the mount-time default range.
   */
  const syncedMeasureRequestSignatureRef = useRef<string | null>(null);

  /** Refetch overview data when the measure request signature changes. */
  useEffect(() => {
    if (syncedMeasureRequestSignatureRef.current === null) {
      syncedMeasureRequestSignatureRef.current = measureRequestSignatureValue;
      return;
    }

    if (
      syncedMeasureRequestSignatureRef.current === measureRequestSignatureValue
    ) {
      return;
    }

    syncedMeasureRequestSignatureRef.current = measureRequestSignatureValue;
    setDefaultRange(defaultIntervals);
  }, [defaultIntervals, measureRequestSignatureValue, setDefaultRange]);

  const thresholdPlotlyOptions = useMemo(() => {
    if (mode !== 'timeline') {
      return undefined;
    }

    const thresholdPrefs = resolvedPlotPreferences.thresholdPrefs;
    const selectedThresholds =
      thresholdPrefs?.request?.selectedThresholdCatalog ?? [];
    const variableMappingsByThresholdId =
      thresholdPrefs?.request?.variableMappingsByThresholdId;

    if (!selectedThresholds.length || !variableMappingsByThresholdId) {
      return undefined;
    }

    const selectedThresholdIdSet = new Set(
      thresholdPrefs.request?.selectedThresholdAssignmentIds ?? []
    );
    const effectiveThresholds = selectedThresholds.filter((threshold) =>
      selectedThresholdIdSet.has(threshold.id)
    );

    if (!effectiveThresholds.length) {
      return undefined;
    }

    const measuredRows = (measureRows ?? []) as Record<string, unknown>[];
    const metadata = transport.getMeasureMetadata?.();

    if (!metadata?.thresholds || !metadata.threshold_breaches) {
      return undefined;
    }

    const variableMappings = effectiveThresholds
      .map((threshold) => {
        const variables = variableMappingsByThresholdId[threshold.id];
        if (!variables) {
          return undefined;
        }

        return {
          thresholdScopeAssignmentId: threshold.id,
          variables,
        };
      })
      .filter(
        (
          item
        ): item is {
          thresholdScopeAssignmentId: number;
          variables: Record<string, string>;
        } => item !== undefined
      );

    if (variableMappings.length === 0) {
      return undefined;
    }

    const coreSettings = buildThresholdPlotSettingsCore({
      thresholds: effectiveThresholds,
      thresholdRepresentations: metadata.thresholds,
      thresholdBreaches: metadata.threshold_breaches,
      measuredRows,
      rowIndexOffset: visibleDataBeginIndex,
      variableMappings,
      styleSelectionByThresholdId:
        thresholdPrefs.visualization?.styleSelectionByThresholdId,
      markerBorderWidthPx: thresholdPrefs.visualization?.markerBorderWidthPx,
    });

    const curveIdByColumn = Object.entries(
      resolvedPlotPreferences.curveToSourceIndex ?? {}
    ).reduce<Record<string, string>>((acc, [curveId, sourceIndex]) => {
      acc[String(sourceIndex)] = curveId;
      return acc;
    }, {});

    Object.keys(resolvedPlotPreferences.curvePrefs).forEach((curveId) => {
      curveIdByColumn[curveId] = curveId;
    });

    const adapted = adaptThresholdSettingsToPlotly(coreSettings, {
      curveIdByColumn,
      xValuesByRowIndex: measuredRows.map((row) => row.Temps),
    });

    return {
      additionalTraces: adapted.thresholdTraces,
      sensorTracePatchesByCurveId: adapted.sensorTracePatchesByCurveId,
    };
  }, [
    measureRows,
    mode,
    resolvedPlotPreferences,
    transport,
    visibleDataBeginIndex,
  ]);

  // optional auto-refresh based on intervals set by the toolbar:
  // will not start until the user sets a first refresh interval
  // and therefore should not impact usecases where no auto-refresh is wanted
  const persistPlotPreferences = useCallback(
    (nextPreferences: typeof resolvedPlotPreferences) => {
      const newConfig = setAdditionalProperties(
        nextPreferences as unknown as JSONSerializable,
        elementData
      );

      dispatch(
        setDashboardElement({
          gridType: grid_type,
          gridId: gridId,
          widgetId: widget_id,
          dashboardElement: newConfig,
        })
      );
    },
    [dispatch, elementData, gridId, grid_type, widget_id]
  );

  const handleTimeSelectionChange = useCallback(
    ([start, end]: readonly [string, string]) => {
      void applyVisibleRangeChange(start, end);
    },
    [applyVisibleRangeChange]
  );

  /** Handle refresh interval change using the {@link useTimeseriesRangeData} hook. */
  const handleRefreshInterval = useCallback(() => {
    // -- prevent accidental refresh when range type is not relative (should not happen, but just in case)
    if (rangeType !== 'relative') {
      return;
    }

    void refresh();
  }, [rangeType, refresh]);

  /** Handle tool bar select time range using the {@link useTimeseriesRangeData} hook. */
  const handleToolBarSelectTimeRange = useCallback(
    (timeUnit: Parameters<typeof buildIntervalFromTimeUnit>[0]) => {
      // -- if the range type is not relative, no-op (we should not be able to call this callback, but just in case)
      if (rangeType !== 'relative') {
        return;
      }

      // -- build the interval from the time unit and default intervals --
      const interval = buildIntervalFromTimeUnit(timeUnit, defaultIntervals);
      const bounds = interval ? resolveDefaultBounds([interval]) : null;

      // -- if the interval is valid, apply the predefined range --
      if (bounds) {
        void applyPredefinedRange(bounds);
      }
    },
    [applyPredefinedRange, defaultIntervals, rangeType]
  );

  const {
    setRefreshInterval,
    stopRefresh,
    emitNextInterval,
    setNextRequestIntervalStart,
    readNextIntervalStart,
  } = useRefreshIntervalFromIntervals({
    intervals: defaultIntervals,
    onIntervalJSONChange: handleRefreshInterval,
  });

  useEffect(() => {
    const maxStartDateIso = readNextIntervalStart();
    const nextRequestStart = findNextRequestIntervalStart(
      data,
      Object.keys(resolvedPlotPreferences.curvePrefs),
      {
        maxStartDate: maxStartDateIso ? new Date(maxStartDateIso) : undefined,
      }
    );

    setNextRequestIntervalStart(nextRequestStart?.toISOString());
  }, [
    data,
    readNextIntervalStart,
    resolvedPlotPreferences.curvePrefs,
    setNextRequestIntervalStart,
  ]);

  /** Handle refresh interval change using the {@link useTimeseriesRangeData} hook. */
  useEffect(() => {
    // -- if the range type changes to non-relative, stop any ongoing refresh process --
    if (rangeType !== 'relative') {
      stopRefresh();
      return;
    }

    // -- same thing if the refresh interval selector becomes disabled --
    if (!resolvedPlotPreferences.toolBarPrefs.useRefreshIntervalSelector) {
      stopRefresh();
      return;
    }

    const selectedRefreshInterval =
      resolvedPlotPreferences.toolBarPrefs.selectedRefreshInterval;

    // -- if no refresh interval is selected, stop the refresh process --
    if (selectedRefreshInterval === undefined) {
      stopRefresh();
      return;
    }

    // -- start the refresh process with the selected refresh interval --
    setRefreshInterval(refreshIntervalOptionToMs(selectedRefreshInterval));
  }, [
    rangeType,
    resolvedPlotPreferences.toolBarPrefs.selectedRefreshInterval,
    resolvedPlotPreferences.toolBarPrefs.useRefreshIntervalSelector,
    setRefreshInterval,
    stopRefresh,
  ]);

  if (isScatterPlot && !additionalProperties) {
    return (
      <div ref={ref} className="widget-content">
        <UnconfiguredWidgetPlaceholder
          message="Le graphique n'est pas encore configuré"
          gridId={gridId}
          gridType={grid_type}
          widgetId={dashboardElementId}
          elementType="plot"
          configPanelState="Reconfiguring"
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent name={plotName} message="Chargement en cours" spinner />
      </div>
    );
  }

  if (loadError) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent name={plotName} message={loadError.message} />
      </div>
    );
  }

  return (
    <div ref={ref} className="widget-content">
      {csvDialog}
      <Plot
        className="plot"
        type={mode === 'timeline' ? 'timeseries' : mode}
        onExportCsv={handleExportCsv}
        initialTimeSelection={visibleInterval}
        loadedTimeSelection={loadedInterval}
        {...plotPreferencesForRender}
        data={data}
        plotlyDataOptions={thresholdPlotlyOptions}
        config={getDefaultConfig(plotName)}
        onToolBarRefreshIntervalChange={(selectedRefreshInterval) => {
          setRefreshInterval(
            refreshIntervalOptionToMs(selectedRefreshInterval)
          );
          persistPlotPreferences(
            updateToolBarPreferences(resolvedPlotPreferences, {
              selectedRefreshInterval,
            })
          );
        }}
        onToolBarRefreshIntervalStop={() => {
          stopRefresh();
          persistPlotPreferences(
            updateToolBarPreferences(resolvedPlotPreferences, {
              selectedRefreshInterval: undefined,
            })
          );
        }}
        onToolBarReload={() => {
          emitNextInterval();
        }}
        onToolBarSelectTimeRange={handleToolBarSelectTimeRange}
        onChangeTimeSelection={handleTimeSelectionChange}
        axisSessionRanges={axisSessionRanges}
        onChangeAxisRange={onChangeAxisRange}
        curveSessionVisibility={curveSessionVisibility}
        onToggleCurveSessionVisibility={toggleCurveSessionVisibility}
        onResetCurveSessionVisibility={resetCurveSessionVisibility}
        onDoubleClick={() => {
          // -- debounce: if the last double click was less than 400ms ago, no-op --
          const now = Date.now();
          if (now - lastDoubleClickMsRef.current < 400) {
            return;
          }
          lastDoubleClickMsRef.current = now;

          resetZoomableAxes();
          resetToDefault();
        }}
      />
    </div>
  );
}

export default forwardRef(ScatterPlot);
